import { useTranslation } from "@vixi/localization";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { RouteOptionsById, RouterIds } from "@vixi/router";
import {
  TextInput,
  LoadingScreen,
  FieldLabel,
  FormDescription,
  FormHeading,
  Button,
  RadioGroup,
  RadioGroupItemWithLabel,
} from "@vixi/ui";
import { type Account } from "@vixi/types";
import { useUserInfo } from "@vixi/api";

export const routeId = "/_app/settings/account-settings" satisfies RouterIds;

export const AccountSettingsPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
};

const accountSettingsSchema = z.object({
  firstName: z.string().min(3),
  lastName: z.string().min(3),
  email: z.string().email(),
  newsLetter: z.enum(["true", "false"]),
});

type AccountSettingsFormValues = z.infer<typeof accountSettingsSchema>;

function RouteComponent() {
  // useGoBack(null); used in the parent route
  const { t } = useTranslation();
  const { data: user, isFetching } = useUserInfo();

  const {
    register,
    formState: { errors, isValid },
    control,
    handleSubmit,
    reset,
  } = useForm<AccountSettingsFormValues>({
    resolver: zodResolver(accountSettingsSchema),
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        newsLetter: "false",
      });
    }
  }, [user]);

  const onSubmit = (data: AccountSettingsFormValues) => {
    // TODO: Implement form submission
    console.log(data);
  };

  if (isFetching) return <LoadingScreen />;

  return (
    <div className="flex justify-center items-center flex-col ">
      <div className="flex justify-center items-center flex-col text-center">
        <FormHeading heading="Settings.AccountSettings.Title" className="w-full  flex flex-col  items-center">
          <FormDescription className="mt-2.75">
            {t("Settings.AccountSettings.Subtitle")}
          </FormDescription>
        </FormHeading>
      </div>
   
      <div className="mt-9.5 flex flex-col justify-center items-center gap-8" onSubmit={handleSubmit(onSubmit)}>
        {/* First name field*/}
        <div>
          <FieldLabel>{t("Account.FirstName")}</FieldLabel>
          <TextInput
            {...register("firstName")}
            // error={!!errors.firstName}
            // helperText={errors.firstName?.message}
          />
        </div>

        {/* Last name field*/}
        <div >
          <FieldLabel>{t("Account.LastName")}</FieldLabel>
          <TextInput
            {...register("lastName")}
            // error={!!errors.lastName}
            // helperText={errors.lastName?.message}
          />
        </div>

        {/* Email field*/}
        <div>
          <FieldLabel>{t("Account.Email")}</FieldLabel>
          <TextInput
            {...register("email")}
            // error={!!errors.email}
            // helperText={errors.email?.message}
          />
        </div>

        {/* News letter field*/}
        <div className="flex justify-center items-center flex-col">
          <FieldLabel>{t("Settings.AccountSettings.NewsLetterFieldLabel")}</FieldLabel>
          <Controller
            name="newsLetter"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <RadioGroup {...field}>
                <RadioGroupItemWithLabel value="true" label={t("Yes")} />
                <RadioGroupItemWithLabel value="false" label={t("No")} />
              </RadioGroup>
            )}
          />
        </div>

        {isValid && (
          <div className="absolute end-20 bottom-10">
            <Button type="submit" variant="contained" size="large" className="px-12">
              {t("Action.SaveChanges")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
